import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useApi } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Edit3, ExternalLink } from "lucide-react";

const APP_VERSION = "v2.1.1";
const APP_PUBLISHED_ON = "20 Jun 2026";

function LegalLink({ label, url, testId }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="flex items-center justify-between py-2.5 text-sm text-primary font-medium hover:underline"
    >
      <span>{label}</span>
      <ExternalLink size={16} />
    </a>
  );
}

function roleLabel(role) {
  if (role === "super_admin") return "Super Admin";
  if (role === "admin") return "Admin";
  if (role === "organiser") return "Organiser";
  return "Member";
}

export default function ProfilePage() {
  const { user, refresh, signOut } = useAuth();
  const api = useApi();
  const nav = useNavigate();
  const isSuperAdmin = (user?.role || "").toLowerCase() === "super_admin";

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [title, setTitle] = useState(user?.masonic_title || "");
  const [grand, setGrand] = useState(user?.grand_rank || "");
  const [prov, setProv] = useState(user?.provincial_rank || "");
  const [dietary, setDietary] = useState(user?.dietary_concerns || "");
  const [saving, setSaving] = useState(false);

  const [changingEmail, setChangingEmail] = useState(false);
  const [emailStep, setEmailStep] = useState("request"); // request | confirm
  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const deleteMatches =
    deleteConfirm.trim().toLowerCase() === (user?.email || "").trim().toLowerCase() &&
    (user?.email || "").length > 0;

  useEffect(() => {
    setName(user?.name || "");
    setTitle(user?.masonic_title || "");
    setGrand(user?.grand_rank || "");
    setProv(user?.provincial_rank || "");
    setDietary(user?.dietary_concerns || "");
  }, [user]);

  const startEdit = () => {
    setName(user?.name || "");
    setTitle(user?.masonic_title || "");
    setGrand(user?.grand_rank || "");
    setProv(user?.provincial_rank || "");
    setDietary(user?.dietary_concerns || "");
    setEditing(true);
  };

  const onSave = async () => {
    if (!name.trim()) return toast.error("Please enter your name.");
    setSaving(true);
    try {
      const r = await api("/auth/profile", {
        method: "PATCH",
        body: JSON.stringify({
          name: name.trim(),
          masonic_title: title.trim(),
          grand_rank: grand.trim(),
          provincial_rank: prov.trim(),
          dietary_concerns: dietary.trim(),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return toast.error(d.detail || "Save failed");
      toast.success("Profile updated");
      await refresh();
      setEditing(false);
    } finally { setSaving(false); }
  };

  const onRequestEmail = async () => {
    const next = newEmail.trim().toLowerCase();
    if (!next) return toast.error("Please enter a new email address.");
    if (next !== confirmEmail.trim().toLowerCase())
      return toast.error("The two email addresses don't match.");
    setEmailBusy(true);
    try {
      const r = await api("/auth/email-change/request", {
        method: "POST",
        body: JSON.stringify({ new_email: next }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return toast.error(d.detail || "Could not start change");
      if (d.dev_otp) { setEmailOtp(d.dev_otp); toast.message(`(Preview) OTP auto-filled: ${d.dev_otp}`); }
      else toast.success(`Verification code sent to ${next}.`);
      setEmailStep("confirm");
    } finally { setEmailBusy(false); }
  };

  const onConfirmEmail = async () => {
    if (emailOtp.trim().length !== 6) return toast.error("Enter the 6-digit code.");
    setEmailBusy(true);
    try {
      const r = await api("/auth/email-change/confirm", {
        method: "POST",
        body: JSON.stringify({ new_email: newEmail.trim().toLowerCase(), code: emailOtp.trim() }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return toast.error(d.detail || "Verification failed");
      toast.success("Email updated");
      await refresh();
      setChangingEmail(false); setEmailStep("request");
      setNewEmail(""); setConfirmEmail(""); setEmailOtp("");
    } finally { setEmailBusy(false); }
  };

  const onLogout = async () => { await signOut(); nav("/login", { replace: true }); };

  const onDelete = async () => {
    if (!deleteMatches) return setDeleteError("The email you typed doesn't match your account.");
    setDeleting(true); setDeleteError(null);
    try {
      const r = await api("/auth/account/delete", {
        method: "POST",
        body: JSON.stringify({ confirm_email: deleteConfirm.trim().toLowerCase() }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setDeleteError(d?.detail || "Delete failed");
        return;
      }
      await signOut();
      nav("/login", { replace: true });
    } finally { setDeleting(false); }
  };

  const u = user || {};

  return (
    <div>
      <PageHeader title="Profile" subtitle="Your Middlesex Connect account" testId="profile-page-header" />

      <div className="grid gap-4 md:grid-cols-3">
        {/* Identity card */}
        <Card className="p-5 md:col-span-2" data-testid="profile-identity">
          <div className="flex items-center gap-4">
            <img src="/mc-crest.png" alt="" className="h-16 w-auto" />
            <div className="flex-1 min-w-0">
              <div className="text-lg font-semibold truncate" data-testid="profile-name">{u.name || "—"}</div>
              <div className="text-sm text-muted-foreground truncate" data-testid="profile-email">{u.email}</div>
              {(u.masonic_title || u.grand_rank || u.provincial_rank) && (
                <div className="text-xs text-muted-foreground mt-1">
                  {[u.masonic_title, u.grand_rank, u.provincial_rank].filter(Boolean).join(" · ")}
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-border flex flex-col items-center text-center" data-testid="profile-gl-ref">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Grand Lodge Ref</div>
            <div className="font-mono text-sm">{u.gl_ref || "— not on file —"}</div>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-1">
            Need this corrected? Please contact an administrator — this field is read-only for members.
          </p>

          <div className="mt-4 flex flex-col items-center">
            <div className="text-sm font-semibold text-accent" style={{ color: "hsl(var(--mc-red))" }}>
              {roleLabel(u.role)}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">via {u.provider || "email"}</div>
          </div>

          {!editing && (
            <div className="mt-4 flex justify-center">
              <Button variant="outline" onClick={startEdit} data-testid="profile-edit">
                <Edit3 size={14} className="mr-2" /> Edit profile
              </Button>
            </div>
          )}
        </Card>

        {/* Email card */}
        <Card className="p-5" data-testid="profile-email-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">Email address</h3>
            {!changingEmail && (
              <button
                onClick={() => setChangingEmail(true)}
                className="text-sm text-primary font-medium hover:underline"
                data-testid="email-change"
              >
                Change
              </button>
            )}
          </div>
          {!changingEmail ? (
            <div className="text-sm">{u.email}</div>
          ) : emailStep === "request" ? (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                We&apos;ll send a 6-digit code to the new address to confirm it&apos;s yours.
              </div>
              <div className="space-y-1">
                <Label>New email</Label>
                <Input type="email" autoComplete="email" value={newEmail}
                       onChange={(e) => setNewEmail(e.target.value)}
                       placeholder="new@pglm.org.uk" data-testid="new-email" />
              </div>
              <div className="space-y-1">
                <Label>Confirm new email</Label>
                <Input type="email" autoComplete="off" value={confirmEmail}
                       onChange={(e) => setConfirmEmail(e.target.value)}
                       placeholder="Retype the new address" data-testid="confirm-email" />
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" className="flex-1" onClick={() => { setChangingEmail(false); setNewEmail(""); setConfirmEmail(""); }} data-testid="email-cancel">
                  Cancel
                </Button>
                <Button className="flex-1" onClick={onRequestEmail} disabled={emailBusy} data-testid="email-send">
                  {emailBusy ? "Sending…" : "Send code"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                Enter the 6-digit code we sent to <span className="font-medium text-foreground">{newEmail}</span>.
              </div>
              <div className="space-y-1">
                <Label>6-digit code</Label>
                <Input inputMode="numeric" maxLength={6}
                       value={emailOtp} onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                       className="text-center tracking-[8px] text-xl" data-testid="email-otp" />
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" className="flex-1" onClick={() => { setEmailStep("request"); setEmailOtp(""); }} data-testid="email-back">
                  Back
                </Button>
                <Button className="flex-1" onClick={onConfirmEmail} disabled={emailBusy} data-testid="email-confirm">
                  {emailBusy ? "Confirming…" : "Confirm change"}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Edit profile card */}
      {editing && (
        <Card className="p-5 mt-4" data-testid="profile-edit-card">
          <h3 className="text-lg font-semibold">Edit your Masonic Profile</h3>
          <p className="text-xs text-muted-foreground mt-1 mb-4">
            Keep your Masonic details up to date to make event bookings quicker and easier, particularly where additional information is required, such as for Annual General Meetings.
          </p>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Full name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="John Smith" data-testid="edit-name" />
            </div>
            <div className="space-y-1">
              <Label>Masonic title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Bro / W.Bro / R.W.Bro / V.W.Bro" data-testid="edit-title" />
              <div className="text-xs text-muted-foreground">
                For consistency, please use the standard format for Masonic titles, such as W Bro, Bro, VW Bro, etc.
              </div>
            </div>
            <div className="space-y-1">
              <Label>Grand Rank</Label>
              <Input value={grand} onChange={(e) => setGrand(e.target.value)} placeholder="PJGD, PSGD, AGDC, etc." data-testid="edit-grand" />
            </div>
            <div className="space-y-1">
              <Label>Provincial Rank</Label>
              <Input value={prov} onChange={(e) => setProv(e.target.value)} placeholder="PrGStdB, PPrAGDC, etc." data-testid="edit-prov" />
            </div>
            <div className="space-y-1">
              <Label>Dietary Concerns</Label>
              <Textarea rows={2} maxLength={256} value={dietary}
                        onChange={(e) => setDietary(e.target.value.slice(0, 256))}
                        placeholder="e.g. Vegetarian, nut allergy" data-testid="edit-dietary" />
              <div className="text-xs text-muted-foreground">
                Please provide details of any dietary requirements, allergies or food-related concerns we should be aware of when catering for you at an event. Keep the information clear and concise so the event organiser can make appropriate arrangements.
              </div>
              <div className="text-xs text-muted-foreground text-right" data-testid="edit-dietary-counter">{dietary.length}/256</div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="ghost" className="flex-1" onClick={() => setEditing(false)} data-testid="edit-cancel">Cancel</Button>
              <Button className="flex-1" onClick={onSave} disabled={saving} data-testid="edit-save">{saving ? "Saving…" : "Save"}</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Legal */}
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Legal</div>
        <Card className="p-4 divide-y divide-border" data-testid="profile-legal">
          <LegalLink
            label="Privacy Policy"
            url="https://a.pglm.org.uk/Middlesex_Connect_Privacy_Policy.html"
            testId="profile-privacy-policy"
          />
          <LegalLink
            label="Terms & Conditions"
            url="https://a.pglm.org.uk/Middlesex_Connect_Terms_and_Conditions.html"
            testId="profile-terms"
          />
        </Card>
      </div>

      {/* About */}
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">About</div>
        <Card className="p-5 bg-muted/40" data-testid="profile-about">
          <div className="text-sm font-semibold" data-testid="about-version">
            Middlesex Connect {APP_VERSION} · Published {APP_PUBLISHED_ON}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">Published by Province of Middlesex</div>
          <div className="text-xs text-muted-foreground">
            Developer W Bro David North ·{" "}
            <a href="mailto:developer@pglm.org.uk" className="text-primary hover:underline" data-testid="about-developer-email">
              developer@pglm.org.uk
            </a>
          </div>
          <div className="border-t border-border my-3" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            © 2026 Province of Middlesex. All rights reserved. Middlesex Connect and its content are the property of the Province of Middlesex and may not be reproduced or distributed without permission. If you discover any errors or omissions, or have ideas for improvements or new features, please report them to the developer. You also have the right to request the removal of your personal information at any time.
          </p>
        </Card>
      </div>

      {/* Sign out */}
      <div className="mt-6">
        <Button
          onClick={onLogout}
          className="w-full text-white"
          style={{ backgroundColor: "hsl(var(--mc-red))" }}
          data-testid="profile-sign-out"
        >
          Sign out
        </Button>
      </div>

      {/* Delete account link */}
      {!isSuperAdmin && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={() => { setDeleteConfirm(""); setDeleteError(null); setDeleteOpen(true); }}
            className="text-xs text-muted-foreground hover:text-destructive underline"
            data-testid="profile-delete-account"
          >
            Delete account
          </button>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={(o) => (deleting ? null : setDeleteOpen(o))}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <div>
                  This cannot be undone. Under UK data protection law (right to be forgotten), tapping{" "}
                  <strong>Permanently delete</strong> will:
                </div>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Remove your name, email, Grand Lodge Ref and Masonic profile (title / Grand Rank / Provincial Rank / dietary concerns) from Middlesex Connect.</li>
                  <li>Anonymise your past booking records so organisers keep the headcount but can no longer see who you were.</li>
                  <li>Sign you out of every device and delete any pending sign-in codes.</li>
                </ul>
                <div>
                  What we <strong>keep</strong>: payment records for events you have already paid for are retained by our payment provider (Stripe) for financial and audit purposes — we cannot delete those on your behalf.
                </div>
                <div>
                  <Label className="text-sm font-medium">Type your email to confirm</Label>
                  <Input
                    className="mt-1"
                    value={deleteConfirm}
                    onChange={(e) => { setDeleteConfirm(e.target.value); if (deleteError) setDeleteError(null); }}
                    placeholder={u.email || "you@example.com"}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    data-testid="delete-account-confirm"
                  />
                  {deleteError && <div className="text-xs text-destructive mt-1">{deleteError}</div>}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); onDelete(); }}
              disabled={!deleteMatches || deleting}
              data-testid="delete-account-confirm-btn"
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {deleting ? "Deleting…" : "Permanently delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
